import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input } from 'rsuite';
import { formatClock12, formatDate, formatLongDate } from '../../utils/timesheet';
import LengthPicker from './LengthPicker';
import { TaskPicker, addToClock } from './TimesheetParts';

const VIEWPORT_MARGIN = 8;

// Log time, floating out from its button: the project, then the length. A note only when the
// task has to have one. `below` opens under the button where there's room, rather than over it.
// `initialTaskId` starts with the project already picked, and goes straight to the length.
// `prefill` ({ description, startTime?, seconds? }) comes from a calendar event: the note and any
// times are filled in, leaving the project to pick. Changing the length moves the end time.
export default function QuickAdd({ date, anchor, tasks, config, onAdd, onClose, below = false, initialTaskId = null, prefill = null }) {
  const [taskId, setTaskId] = useState(initialTaskId);
  const [seconds, setSeconds] = useState(prefill?.seconds || null);
  const [note, setNote] = useState(prefill?.description || '');
  const [saving, setSaving] = useState(false);
  const [place, setPlace] = useState(null); // { left, top }
  const formRef = useRef(null);
  const hoursRef = useRef(null);
  const addRef = useRef(null);
  const noteId = useId();
  const noteRef = useRef(null);
  // On to the first thing still to fill in: the note if there is one to write, then the length,
  // then Add, as when a calendar event has filled in both.
  const focusNext = () => {
    const note = noteRef.current;
    if (note && !note.value.trim()) note.focus();
    else if (!hoursRef.current?.value) hoursRef.current?.focus();
    else addRef.current?.focus();
  };
  const savingRef = useRef(false);
  savingRef.current = saving;

  const task = tasks.find((t) => t.id === taskId);
  const exempt = new Set(config?.descriptionExemptRoles || []);
  const needsNote = Boolean(task && config?.settings?.requireDescription) && !exempt.has(task?.systemRole);
  const canAdd = Boolean(taskId) && seconds !== null && (!needsNote || note.trim()) && !saving;
  const prefillEnd = prefill?.startTime && seconds !== null ? addToClock(prefill.startTime, seconds) : null;

  // On the side asked for if it fits, otherwise the other, and always on screen. Again when the
  // page moves under it, or the form grows.
  useLayoutEffect(() => {
    const position = () => {
      const button = anchor.getBoundingClientRect();
      const form = formRef.current.getBoundingClientRect();
      const above = button.top - form.height - VIEWPORT_MARGIN;
      const under = button.bottom + VIEWPORT_MARGIN;
      const fitsAbove = above >= VIEWPORT_MARGIN;
      const fitsUnder = under + form.height <= window.innerHeight - VIEWPORT_MARGIN;
      const top = (below ? fitsUnder || !fitsAbove : !fitsAbove) ? under : above;
      setPlace({
        left: Math.max(VIEWPORT_MARGIN, Math.min(button.left, window.innerWidth - form.width - VIEWPORT_MARGIN)),
        top: Math.max(VIEWPORT_MARGIN, Math.min(top, window.innerHeight - form.height - VIEWPORT_MARGIN)),
      });
    };
    position();
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
    };
  }, [anchor, below, needsNote]);

  // Once it's placed: a browser won't focus anything while the form is still hidden.
  const placed = Boolean(place);
  useEffect(() => {
    if (initialTaskId && placed) focusNext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTaskId, placed]);

  // A click anywhere else closes it. The task menu and the length panel float outside the form but
  // belong to it, and the button has its own click, which toggles.
  useEffect(() => {
    const away = (e) => {
      if (savingRef.current || formRef.current?.contains(e.target) || anchor.contains(e.target)) return;
      // Nor does the tutorial's card, which is talking about it.
      if (e.target.closest?.('.ts-tp-popup, .ts-clock-panel, .nsi-tutorial')) return;
      onClose();
    };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  }, [anchor, onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canAdd) return;
    setSaving(true);
    try {
      await onAdd({
        taskId,
        date,
        durationSeconds: seconds,
        description: note.trim(),
        ...(prefill?.startTime ? { startTime: prefillEnd ? prefill.startTime : null, endTime: prefillEnd } : {}),
      });
      onClose();
    } catch {
      setSaving(false);
    }
  };

  // Not one from inside the task menu, which only closes the menu.
  const escape = (e) => {
    if (e.key === 'Escape' && !saving && !e.target.closest?.('.ts-tp-popup')) onClose();
  };

  return createPortal(
    <form
      ref={formRef}
      className="ts-quick"
      style={place || { visibility: 'hidden' }}
      onSubmit={submit}
      onKeyDown={escape}
      aria-label={`Log time on ${formatLongDate(date)}`}
    >
      <span className="ts-quicktitle">
        Log time, {formatDate(date, { weekday: 'short', month: 'short', day: 'numeric' })}
        {prefillEnd ? `, ${formatClock12(prefill.startTime)} – ${formatClock12(prefillEnd)}` : ''}
      </span>
      {/* Remade once placed, so its menu opens against where the field ended up. */}
      <TaskPicker
        key={place ? 'placed' : 'placing'}
        tasks={tasks}
        value={taskId}
        defaultOpen={Boolean(place) && !taskId}
        onChange={(value) => {
          setTaskId(value);
          // After the menu has closed and given focus back to the picker, and the task's note
          // field, if it needs one, has appeared.
          if (value) setTimeout(focusNext, 60);
        }}
        placeholder="Project / task"
        aria-label="Project / task"
      />
      {needsNote || prefill ? (
        <div className="ts-logcard__field">
          <label className="ts-logcard__label" htmlFor={noteId}>Note</label>
          <Input inputRef={noteRef} id={noteId} value={note} onChange={setNote} aria-label="Note" />
        </div>
      ) : null}
      <div className="ts-quickrow">
        <div className="ts-logcard__field ts-quickrow__length">
          <span className="ts-logcard__label">Length</span>
          <LengthPicker
            inputRef={hoursRef}
            value={seconds}
            onChange={setSeconds}
            step={config?.settings?.loggingIntervalMinutes}
            aria-label="Length"
          />
        </div>
        <button ref={addRef} type="submit" className="bdc-btn bdc-btn--primary" disabled={!canAdd}>{saving ? 'Adding…' : 'Add'}</button>
        <button type="button" className="bdc-btn" onClick={onClose} disabled={saving}>Cancel</button>
      </div>
    </form>,
    document.body
  );
}
